import { HeroLiveIndicator } from "@landing/components/landing/hero-live-indicator"
import { HeroSportEmoji } from "@landing/components/landing/hero-sport-emoji"
import { LandingRotatingPlatformCard } from "@landing/components/landing/LandingRotatingPlatformCard"
import {
  getClipForSport,
  getSloganClip,
  introBaseballClip,
  introBasketballClip,
  introFootballClip,
  introGolfClip,
  introHockeyClip,
  introTennisClip,
  playSingleAudio,
} from "@landing/components/landing/LandingSounds"
import sportsEmojis from "@landing/data/sports-emojis.json"
import { useMediaQuery } from "@landing/hooks/use-media-query"
import { motion } from "framer-motion"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { brackyAnimations } from "./LandingBrackyAvatar"
import "./hero-styles.css"

export const LandingSectionHero = React.memo(function LandingSectionHero() {
  const [isScrolling, setIsScrolling] = useState(false)
  const [scrollRotation, setScrollRotation] = useState(0)
  // Detect medium-large and smaller breakpoints for 7% size increase, lg+ gets 1% increase
  const isMdLgOrSmaller = useMediaQuery("(max-width: 1024px)")
  // liveStage controls border/dot animation phases: 'dim' -> 'flicker' -> 'glow'
  type LiveStage = "dim" | "flicker" | "glow"
  const [_liveStage, setLiveStage] = useState<LiveStage>("dim")
  // Track if the viewport is at the very top so we can hide the bottom emoji when at the top
  const [isAtTop, setIsAtTop] = useState(true)
  // Track image loading state for debugging - using Maps for multiple images
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set())
  const [imageLoaded, setImageLoaded] = useState<Set<string>>(new Set())
  // Track LIVE logo state for slogan playback
  const [isPlayingSlogan, setIsPlayingSlogan] = useState(false)
  // Track which sport was randomly selected to play after slogan
  const [selectedSportForSequence, setSelectedSportForSequence] = useState<string | null>(null)
  // Track dot blink state for indicating new sound
  const [dotBlink, setDotBlink] = useState(false)

  /* --------------------------------------------------------------------
   * Dynamic scale logic for the "LIVE" indicator
   *
   * 1. multiplier         – becomes 0.94 while scrolling *down* (indicator shrinks)
   *                         and 1.06 while scrolling *up*  (indicator grows).
   *                         It eases back to 1 once scrolling stops.
   * 2. computedLiveScale  – multiplier => inline transform on the indicator.
   * -------------------------------------------------------------------*/

  const [liveMultiplier, setLiveMultiplier] = useState<number>(1)
  const prevScrollYRef = useRef<number>(typeof window !== "undefined" ? window.scrollY : 0)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const tickingRef = useRef(false)

  // Scroll listener to determine direction and set scale multiplier (inverse of Bracky)
  useEffect(() => {
    if (typeof window === "undefined") return

    const handleScroll = () => {
      if (tickingRef.current) return

      tickingRef.current = true
      requestAnimationFrame(() => {
        const currentY = window.scrollY
        const delta = currentY - prevScrollYRef.current

        if (Math.abs(delta) > 8) {
          if (delta > 0) {
            // Scrolling down → shrink (half previous amount) & lower opacity
            setLiveMultiplier(0.97)
          } else {
            // Scrolling up → gentle grow (half previous amount)
            setLiveMultiplier(1.0225)
          }
        }

        prevScrollYRef.current = currentY

        // Reset multiplier when scrolling stops
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
        scrollTimeoutRef.current = setTimeout(() => {
          setLiveMultiplier(1)
        }, 220) as unknown as NodeJS.Timeout

        tickingRef.current = false
      })
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    }
  }, [])

  const computedLiveScale = liveMultiplier

  useEffect(() => {
    let scrollTimeout: ReturnType<typeof setTimeout>

    const handleScroll = () => {
      setIsScrolling(true)
      // Update rotation based on scroll position
      const scrollY = window.scrollY
      const rotationDegrees = scrollY * 0.8 // Adjust multiplier to control rotation speed
      setScrollRotation(rotationDegrees)

      // Update "at-top" status
      setIsAtTop(window.scrollY < 10)

      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false)
      }, 150)
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
      clearTimeout(scrollTimeout)
    }
  }, [])

  // Activate the live indicator shortly after the initial emoji burst animation.
  // We deliberately allow this effect to run on every mount (including the
  // development-only double-mount caused by React Strict Mode).  We cancel the
  // callbacks on unmount so that only the final, "real" mount triggers the
  // flicker → glow sequence.
  useEffect(() => {
    const activationDelayMs = 250 // ms before flicker starts
    const flickerDurationMs = 200 // ms length of the flicker sequence

    let isUnmounted = false

    setTimeout(() => {
      if (!isUnmounted) setLiveStage("flicker")
    }, activationDelayMs)

    setTimeout(() => {
      if (!isUnmounted) setLiveStage("glow")
    }, activationDelayMs + flickerDurationMs)

    return () => {
      isUnmounted = true // prevent state updates after unmount
    }
  }, [])

  // 🔊 Play a Bracky notification sound when the avatar is clicked elsewhere on the page has been removed to prevent double audio.

  const heroRef = useRef<HTMLDivElement | null>(null)
  const centerX = 0
  const centerY = 0
  const radius = 134

  const [activeSport, setActiveSport] = useState<string | null>(null)

  // Memoize emoji positions to prevent recalculation on every render
  const emojiPositions = useMemo(() => {
    return sportsEmojis.map((sport, index) => {
      const startAngle = -Math.PI / 2
      const angle = startAngle + (index / sportsEmojis.length) * 2 * Math.PI
      const x = centerX + radius * Math.cos(angle)
      const y = centerY + radius * Math.sin(angle)
      return { sport, index, x, y }
    })
  }, [])

  // Memoize motion transition to prevent new object on every render
  const motionTransition = useMemo(
    () => ({
      type: "spring" as const,
      damping: 20,
      stiffness: 100,
    }),
    [],
  )

  // Handle clicks on the rotating sport emojis
  const handleSportClick = useCallback((sport: { emoji: string; name: string; isImage?: boolean }) => {
    // Trigger Bracky's double nod animation
    brackyAnimations.playDoubleNod()

    // Reset any existing active states to prevent multiple blinking emojis
    setActiveSport(null)
    setSelectedSportForSequence(null)
    setIsPlayingSlogan(false)
    setDotBlink(false)

    // Special handling for basketball, golf, tennis, hockey, baseball, and football: play intro then moment
    if (
      sport.name === "basketball" ||
      sport.name === "golf" ||
      sport.name === "tennis" ||
      sport.name === "hockey" ||
      sport.name === "baseball" ||
      sport.name === "football"
    ) {
      // Trigger camera flash and caret cluster effects when sound plays
      window.dispatchEvent(new Event("sportsEmojiClick"))

      // Highlight the clicked sport during the entire sequence
      setActiveSport(sport.name)

      // Special handling for football: scroll to brackets section and enable music mode
      if (sport.name === "football") {
        // Smooth-scroll to the Brackets section if it exists on the page
        const bracketSection = document.getElementById("bracket")
        bracketSection?.scrollIntoView({ behavior: "smooth" })

        // Dispatch a global event so the Bracket section can enter "music mode"
        window.dispatchEvent(new Event("activateMusicMode"))
      }

      // Play the appropriate intro clip first
      const introClip =
        sport.name === "basketball"
          ? introBasketballClip
          : sport.name === "golf"
            ? introGolfClip
            : sport.name === "tennis"
              ? introTennisClip
              : sport.name === "hockey"
                ? introHockeyClip
                : sport.name === "baseball"
                  ? introBaseballClip
                  : introFootballClip
      playSingleAudio(introClip, 0.9)

      if (typeof window !== "undefined" && window.activeCallAudio) {
        const introAudio = window.activeCallAudio
        const handleIntroEnded = () => {
          // After intro ends, play a random moment for the sport
          const momentClip = getClipForSport(sport.name)
          if (momentClip) {
            playSingleAudio(momentClip, 0.9)

            // Set up listener for the moment clip
            if (typeof window !== "undefined" && window.activeCallAudio) {
              const momentAudio = window.activeCallAudio
              const handleMomentEnded = () => {
                // Clear the active sport after the moment finishes
                setActiveSport((prev) => (prev === sport.name ? null : prev))
                momentAudio.removeEventListener("ended", handleMomentEnded)
              }
              momentAudio.addEventListener("ended", handleMomentEnded)
            }
          } else {
            // If no moment clip, just clear the active sport
            setActiveSport((prev) => (prev === sport.name ? null : prev))
          }

          introAudio.removeEventListener("ended", handleIntroEnded)
        }
        introAudio.addEventListener("ended", handleIntroEnded)
      }
    } else {
      // For other sports, use the regular clip
      const clip = getClipForSport(sport.name)
      if (clip) {
        // Trigger camera flash and caret cluster effects when sound plays
        window.dispatchEvent(new Event("sportsEmojiClick"))

        // Highlight the clicked sport until its audio finishes playing
        setActiveSport(sport.name)
        playSingleAudio(clip, 0.9)
        // Attach a one-time listener so we can clear the spotlight when the clip ends
        if (typeof window !== "undefined" && window.activeCallAudio) {
          const audio = window.activeCallAudio
          const handleEnded = () => {
            // Only clear if this audio corresponds to the current active sport
            setActiveSport((prev) => (prev === sport.name ? null : prev))
            audio.removeEventListener("ended", handleEnded)
          }
          audio.addEventListener("ended", handleEnded)
        }
      }
    }
  }, [])

  const handleImageError = useCallback((emoji: string) => {
    setImageErrors((prev) => new Set([...prev, emoji]))
  }, [])

  const handleImageLoad = useCallback((emoji: string) => {
    setImageLoaded((prev) => new Set([...prev, emoji]))
  }, [])

  const handleFootballHover = useCallback((hovering: boolean) => {
    if (hovering) {
      brackyAnimations.playSideEye(true)
    } else {
      brackyAnimations.resetAnimations()
    }
  }, [])

  const handleLiveClick = useCallback(() => {
    setActiveSport(null)
    setSelectedSportForSequence(null)
    setIsPlayingSlogan(false)
    setDotBlink(true)
    setTimeout(() => setDotBlink(false), 400)

    // Play the "Live the Game" slogan sound when LIVE logo is clicked
    const liveTheGameClip = "https://a.bracky.app/assets/landing/sounds/slogans/live-the-game-1.mp3"
    playSingleAudio(liveTheGameClip, 0.8)
  }, [])

  // Observe hero visibility to inform background blend mode
  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window) || !heroRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        window.dispatchEvent(new CustomEvent("landingHeroVisible", { detail: { isVisible: entry.isIntersecting } }))
      },
      { threshold: 0.25 },
    )

    observer.observe(heroRef.current)

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={heroRef}
      className="relative min-h-88 mt-0 sm:-mt- md:-mt-2 lg:-mt-1 xl:mt-0 2xl:mt-0 md:min-h-96 lg:min-h-112 xl:min-h-136 2xl:min-h-168 flex items-center justify-center px-6 scale-100 lg:scale-[1.05] xl:scale-[1.20] 2xl:scale-[1.35] transition-transform duration-300 ease-out"
    >
      {/* Primary hero button – only this scales on active */}
      <button
        type="button"
        className="group relative z-20 cursor-pointer focus:outline-hidden"
        onPointerDown={(_e) => {
          brackyAnimations.playDoubleNod()

          // Reset any existing active states to prevent multiple blinking emojis
          setActiveSport(null)
          setDotBlink(false)

          // Removed automatic sport selection – slogan will play on release, no moment clip.
        }}
        onPointerUp={(_e) => {
          // Play a single random slogan; no follow-up sport moment.
          const sloganClip = getSloganClip()
          setIsPlayingSlogan(true)
          playSingleAudio(sloganClip, 0.8)

          if (typeof window !== "undefined" && window.activeCallAudio) {
            const audio = window.activeCallAudio

            // When the slogan finishes, stop the LIVE indicator pulse.
            const handleEnded = () => {
              setIsPlayingSlogan(false)
              audio.removeEventListener("ended", handleEnded)
            }
            audio.addEventListener("ended", handleEnded)
          }
        }}
        aria-label="Interact with hero section"
      >
        {/* Main hero text */}
        <div className="text-center pointer-events-none">
          {/* <h1  delete later
            className="font-extrabold bg-linear-to-b from-[#ffffff] to-[#ddf7e6] text-transparent bg-clip-text -mb-[.31rem]"
            style={{ fontSize: '50px', letterSpacing: '-2px' }}
          >
            Trade
          </h1> */}

          <h1
            className="font-extrabold bg-linear-to-b from-[#ffffff] to-[#ddf7e6] text-transparent bg-clip-text -mb-[.31rem]"
            style={{ fontSize: "50px", letterSpacing: "-2px" }}
          >
            Bet
          </h1>

          <HeroLiveIndicator
            isPlayingSlogan={isPlayingSlogan}
            activeSport={activeSport}
            computedLiveScale={computedLiveScale}
            liveMultiplier={liveMultiplier}
            dotBlink={dotBlink}
            onLiveClick={handleLiveClick}
          />

          <h1
            className="font-extrabold text-white flex items-center justify-center gap-1 mt-.5"
            style={{ fontSize: "50px", letterSpacing: "-2px" }}
          >
            <span className="bg-linear-to-b from-[#ddf7e6] to-[#e2e5fe] text-transparent bg-clip-text">on</span>
            {/* Rotating platform card */}
            <div className="relative top-[0.35rem] left-[0.35rem]">
              <LandingRotatingPlatformCard />
            </div>
          </h1>
        </div>
      </button>

      {/* Animated sports emojis in circle – outside the button so they don't trigger its :active state */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none z-30"
        animate={{ rotate: scrollRotation }}
        transition={motionTransition}
      >
        {emojiPositions.map(({ sport, index, x, y }) => (
          <HeroSportEmoji
            key={sport.name}
            sport={sport}
            index={index}
            x={x}
            y={y}
            isScrolling={isScrolling}
            isMdLgOrSmaller={isMdLgOrSmaller}
            activeSport={activeSport}
            isPlayingSlogan={isPlayingSlogan}
            selectedSportForSequence={selectedSportForSequence}
            isAtTop={isAtTop}
            imageErrors={imageErrors}
            imageLoaded={imageLoaded}
            onSportClick={handleSportClick}
            onImageError={handleImageError}
            onImageLoad={handleImageLoad}
            onFootballHover={handleFootballHover}
          />
        ))}
      </motion.div>
    </div>
  )
})
